// lib
import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import ClickOutside from '@aytm/lib/rfd-ui/components/basic/click-outside'

// styles
import style from '@aytm/app/stats/page/sidebar/components/styles/SidebarPopup.module.scss'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// Opens downwards from the target while the whole help fits below it inside the sidebar, and otherwise
// towards the side with more room. The height judged is the one this popup's help was drawn at; until it
// has drawn once, it opens downwards. The room is counted inside the part of the root on screen: the root can
// be the whole report, which runs thousands of pixels past the window, and room nobody can see is no room.
export const calcPopupDirection = (targetRect, rootRect, height, viewportHeight = window.innerHeight) => {
  if (!(0 < height)) return 'righttop'

  const rootTop = Math.max(rootRect.y, 0)
  const rootBottom = Math.min(rootRect.y + rootRect.height, viewportHeight)
  const below = rootBottom - targetRect.y
  const above = targetRect.y + targetRect.height - rootTop

  if (height <= below) return 'righttop'

  return below >= above ? 'righttop' : 'rightbottom'
}

const SidebarPopup = ({
  children,
  id,
  testId = undefined != id ? `stat-sidebar-help-${id}` : undefined,
  root,
  help,
  width = '600px',
  trigger = 'click',
  direction = 'righttop'
}) => {
  const [show, setShow] = useState(false)

  const [popupDirection, setPopupDirection] = useState(direction)
  // the first opening is drawn unseen until its help is measured and its direction decided
  const [measuring, setMeasuring] = useState(false)

  const popupRef = useRef(null)
  const [popupTarget, setPopupTarget] = useState(null)

  const popupTimerRef = useRef(null)
  const hoverTimerRef = useRef(null)
  const measureFrameRef = useRef(null)

  const directionRef = useRef(null)
  directionRef.current = direction

  // the height this popup's help was drawn at; every help is its own length, so it is not shared
  const heightRef = useRef(null)

  // A group folding above the trigger moves it without a scroll or a resize, so the direction is also
  // decided as the popup opens, from where the trigger stands then.
  const decideDirection = useCallback(() => {
    if (!root || !popupTarget || 'righttop' != directionRef.current) return

    setPopupDirection(
      calcPopupDirection(popupTarget.getBoundingClientRect(), root.getBoundingClientRect(), heightRef.current)
    )
  }, [root, popupTarget])

  // The help's height is read off the popup once it has drawn, and the direction decided again with it. The
  // popup draws its content into a container it attaches and places a moment later, so the reading waits a
  // few frames for it.
  const measureHelp = useCallback(
    node => {
      if (!node || null != heightRef.current) return

      let frames = 10
      const measure = () => {
        const body = node.closest('.rfd-ui-popup')
        const placed = body && !body.classList.contains('rfd-ui-popup-hidden')
        const height = placed ? body.getBoundingClientRect().height : 0

        if (0 < height) {
          heightRef.current = height
          decideDirection()
          setMeasuring(false)
        } else if (0 < frames--) measureFrameRef.current = requestAnimationFrame(measure)
        // a popup that never reports a height is shown as it was drawn rather than left unseen
        else setMeasuring(false)
      }
      measure()
    },
    [decideDirection]
  )

  const delaySetShow = useCallback(
    value => {
      clearTimeout(hoverTimerRef.current)

      if (value) {
        decideDirection()
        if (null == heightRef.current) setMeasuring(true)
        setShow(true)
      } else hoverTimerRef.current = setTimeout(() => setShow(false), 100)

      return () => clearTimeout(hoverTimerRef.current)
    },
    [setShow, decideDirection]
  )

  const toggleShow = () => {
    if (!show) {
      decideDirection()
      if (null == heightRef.current) setMeasuring(true)
    }
    setShow(!show)
  }

  const mouseOverCallback = useCallback(() => {
    clearTimeout(hoverTimerRef.current)

    setShow(true)
  }, [setShow])
  const mouseOutCallback = useCallback(() => {
    clearTimeout(hoverTimerRef.current)

    hoverTimerRef.current = setTimeout(() => setShow(false), 100)

    return () => clearTimeout(hoverTimerRef.current)
  }, [setShow])

  const scrollCallback = useCallback(() => setShow(false), [setShow])

  const detachScroll = useCallback(() => {
    window.removeEventListener('scroll', scrollCallback)
    window.removeEventListener('resize', scrollCallback)
    root && root.removeEventListener('scroll', scrollCallback)
  }, [root, scrollCallback])

  // Unmounting while the popup is open never reaches onHide, so the same detach runs here —
  // otherwise the scroll listener outlives the component and hides an unmounted popup.
  useEffect(
    () => () => {
      clearTimeout(hoverTimerRef.current)
      clearTimeout(popupTimerRef.current)
      cancelAnimationFrame(measureFrameRef.current)
      detachScroll()
    },
    [detachScroll]
  )

  // the trigger opens the help from the keyboard, so the keyboard closes it too; a reader who was on
  // the trigger or inside the help is put back on the trigger
  useEffect(() => {
    if (!show) return

    const onKeyDown = event => {
      if ('Escape' !== event.key) return

      const popup = popupRef.current?.refPopup?.refPopup
      const hadFocus = popupTarget === document.activeElement || popup?.contains(document.activeElement)
      clearTimeout(hoverTimerRef.current)
      setShow(false)
      hadFocus && popupTarget?.focus?.()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [show, popupTarget])

  const onScroll = useCallback(() => {
    clearTimeout(popupTimerRef.current)

    if (root && popupTarget) {
      popupTimerRef.current = setTimeout(() => {
        if (!root || !popupTarget) return

        const rootRect = root.getBoundingClientRect()
        const popupTargetRect = popupTarget.getBoundingClientRect()

        setPopupDirection(calcPopupDirection(popupTargetRect, rootRect, heightRef.current))
      }, 100)
    } else setPopupDirection('righttop')

    return () => clearTimeout(popupTimerRef.current)
  }, [root, popupTarget])

  useEffect(() => {
    if ('righttop' != directionRef.current) return

    clearTimeout(popupTimerRef.current)

    if (root) {
      root.removeEventListener('scroll', onScroll, false)

      root.addEventListener('scroll', onScroll, false)
    }

    document.removeEventListener('scroll', onScroll, false)
    document.defaultView.removeEventListener('resize', onScroll, false)

    document.addEventListener('scroll', onScroll, false)
    document.defaultView.addEventListener('resize', onScroll, false)

    onScroll()

    return () => {
      if (root) {
        root.removeEventListener('scroll', onScroll, false)
      }

      document.removeEventListener('scroll', onScroll, false)
      document.defaultView.removeEventListener('resize', onScroll, false)
    }
  }, [root, popupTarget, onScroll])

  const renderHelp = () => (
    <div ref={measureHelp}>
      {React.isValidElement(help) ? React.cloneElement(help) : <div dangerouslySetInnerHTML={{ __html: help }}></div>}
    </div>
  )

  return (
    <Popup
      ref={popupRef}
      className={measuring ? `PRAYTMTheme ${style.measuring}` : 'PRAYTMTheme'}
      targetClassName={id != undefined ? `pendo-help-stat-${id}` : ''}
      width={width}
      direction={popupDirection}
      popupOffset={9}
      portal
      show={show}
      hideOtherPopupsOnShow
      content={() =>
        'click' == trigger ? (
          <ClickOutside
            style={{ display: 'inline-block' }}
            onClickOutside={event => {
              if (
                popupRef.current.refPopupTarget.contains(event.target) ||
                event.target.isSameNode(popupRef.current.refPopupTarget) ||
                popupRef.current.refPopup.refPopup.contains(event.target) ||
                event.target.isSameNode(popupRef.current.refPopup.refPopup)
              )
                return
              setShow(false)
            }}
          >
            {renderHelp()}
          </ClickOutside>
        ) : (
          renderHelp()
        )
      }
      onShow={() => {
        if ('hover' == trigger) {
          popupRef.current.refPopup.refPopup.addEventListener('mouseenter', mouseOverCallback)
          popupRef.current.refPopup.refPopup.addEventListener('mouseleave', mouseOutCallback)
        }

        window.addEventListener('scroll', scrollCallback)
        window.addEventListener('resize', scrollCallback)
        root && root.addEventListener('scroll', scrollCallback)
      }}
      onHide={detachScroll}
    >
      {React.isValidElement(children) ? (
        React.cloneElement(children, {
          ref: setPopupTarget,
          onMouseEnter: 'hover' == trigger ? () => delaySetShow(true) : null,
          onMouseLeave: 'hover' == trigger ? () => delaySetShow(false) : null,
          onClick: 'click' == trigger ? toggleShow : null
        })
      ) : (
        <i
          ref={setPopupTarget}
          data-testid={testId}
          className="icon-aytmfd-help-v2"
          // a help icon that opens on click is a button named Help; a hover-only one stays a plain mark
          aria-label={'click' == trigger ? 'Help' : undefined}
          onMouseEnter={'hover' == trigger ? () => delaySetShow(true) : null}
          onMouseLeave={'hover' == trigger ? () => delaySetShow(false) : null}
          {...pressable('click' == trigger ? toggleShow : null)}
        />
      )}
    </Popup>
  )
}

SidebarPopup.propTypes = {
  children: PropTypes.any,
  id: PropTypes.string,
  testId: PropTypes.string,
  root: PropTypes.any,
  help: PropTypes.any,
  width: PropTypes.string,
  trigger: PropTypes.string,
  direction: PropTypes.string
}

SidebarPopup.displayName = 'SidebarPopup'

export default SidebarPopup
